import { ReactNode } from 'react'
import { TimelineEventType, TimelineEvent, TimelineEventInitiator } from 'screens/Activity/Types'
import { ActivityActionModalContextValue } from 'screens/Activity/components/ActionModal/ActivityActionModalContext'
import {
  InformalFeedbackReviewState,
  InformalFeedbackReviewScore,
  InformalFeedbackReviewDismissReason,
} from 'api/clients/InformalFeedbackRequestsClient'
import { ComparePosition } from '../../screens/Compare/Types'
import { RevisionType } from 'components/BlazeCommandLine/Types'
import { RevisionAcceptAction } from 'hooks/queries/layers/revisions/useAcceptRevision'

/*
 * List of Activity Events. Type name must be `{EventName}Event`
 */

/*
 * Edits
 */

export interface EditsEvent {
  id: string
  user_id: number | null
  user_avatar_url: string | null
  user_display_name: string | null
  timestamp: string
  steps: {
    step_id: number
    t: string
    version: number
  }[]
}

export interface EditsGroupEvent extends TimelineEvent {
  type: TimelineEventType.EditsGroup
  user_count: number
  data: EditsEvent[]
}

/*
 * Comments
 */
export enum CommentableType {
  ARTICLE = 'Article',
  TODO = 'Todo',
  COMMENT = 'Comment',
}

export interface CommentsGroupComment {
  timestamp: string
  comment_id: number
  commentable_id?: number
  comment: string
  html_comment: string | null
  commentable_type: CommentableType
  user: {
    id: number
    type: 'User' | 'AnonymousUser'
    display_name: string | null
    avatar: string | null
  }
}

export interface CommentsGroupEvent extends TimelineEvent {
  type: TimelineEventType.CommentsGroup
  data: CommentsGroupComment[]
  commenters: number
}

export interface RepliesGroupEvent extends TimelineEvent {
  type: TimelineEventType.RepliesGroup
  data: CommentsGroupComment[]
  commenters: number
}

/*
 * Suggestions
 */
export interface ArticleSuggestionReviewsCreatedEvent extends TimelineEvent {
  type: TimelineEventType.ArticleSuggestionReviewsCreated
  user: TimelineEventInitiator
  data: {
    base_branch: {
      key: string
      title: string
      url: string
    }
    branch: {
      key: string
      title: string
      url: string
    }
    iteration: number
    message: string | null
  }
}

export interface ArticleSuggestionReviewRejectedEvent extends TimelineEvent {
  type: TimelineEventType.ArticleSuggestionReviewRejected
  user: TimelineEventInitiator
  data: {
    message: string | null
    suggester: {
      avatar_url: string | null
      display_name: string | null
      id: number
    }
  }
}

export interface ArticleSuggestionApprovedAndMergedEvent extends TimelineEvent {
  type: TimelineEventType.ArticleSuggestionApprovedAndMerged
  user: TimelineEventInitiator
  data: {
    message: string | null
    suggester: {
      avatar_url: string | null
      display_name: string | null
      id: number
    }
    base_branch: {
      key: string
      title: string
      url: string
    }
    branch: {
      key: string
      title: string
      url: string
    }
  }
}

export interface ArticleSuggestionCancelledEvent extends TimelineEvent {
  type: TimelineEventType.ArticleSuggestionCancelled
  user: TimelineEventInitiator
  data: {
    suggester: {
      avatar_url: string | null
      display_name: string | null
      id: number
    }
    base_branch: {
      key: string
      title: string
      url: string
    }
    branch: {
      key: string
      title: string
      url: string
    }
  }
}

/*
 * Informal feedback requests
 */
export interface ArticleInformalFeedbackReviewsCreatedEvent extends TimelineEvent {
  type: TimelineEventType.ArticleInformalFeedbackReviewsCreated
  user: TimelineEventInitiator
  data: {
    request_id: number
    request: {
      id: number
      key: string
      description: string | null
      html_description: string | null
    }
    requester: {
      id: number | null
      display_name: string
    }
    reviews: {
      id: number
      reviewer: {
        id: number | null
        display_name: string
      }
      state: InformalFeedbackReviewState
      iteration: number
    }[]
    branch?: {
      key: string
      title: string
      url: string
    }
    message: string | null
    initial: boolean
  }
}

export interface ArticleInformalFeedbackViewersCreatedEvent extends TimelineEvent {
  type: TimelineEventType.ArticleInformalFeedbackViewersCreated
  user: TimelineEventInitiator
  data: {
    request_id: number
    request: {
      id: number
      key: string
      description: string | null
      html_description: string | null
    }
    requester: {
      id: number | null
      display_name: string
    }
    viewers: {
      id: number | null
      display_name: string
    }[]
    message: string | null
  }
}

export interface ArticleInformalFeedbackViewersCoCreatedEvent
  extends Omit<ArticleInformalFeedbackViewersCreatedEvent, 'type'> {
  type: TimelineEventType.ArticleInformalFeedbackViewersCoCreated
}

export interface ArticleInformalFeedbackReviewSubmittedEvent extends TimelineEvent {
  type: TimelineEventType.ArticleInformalFeedbackReviewSubmitted
  user: TimelineEventInitiator
  data: {
    reviewer: {
      display_name: string | null
    }
    requester: {
      display_name: string | null
    }
    request_id: number
    message: string | null
    score: InformalFeedbackReviewScore | null
  }
}

export interface ArticleInformalFeedbackReviewDismissedEvent extends TimelineEvent {
  type: TimelineEventType.ArticleInformalFeedbackReviewDismissed
  user: TimelineEventInitiator
  data: {
    reviewer: {
      display_name: string | null
    }
    requester: {
      display_name: string | null
    }
    request_id: number
    message: string | null
    reason: InformalFeedbackReviewDismissReason | null
  }
}

export enum ArticleInformalFeedbackRequestClosedEventReason {
  Expired = 'expired',
  UserEnded = 'user_ended',
  AllReviewersApproved = 'all_reviewers_approved',
  AllReviewersDismissed = 'all_reviewers_dismissed',
  ArticleDestroyed = 'article_destroyed',
  DecisionMade = 'decision_made',
}

export interface ArticleInformalFeedbackRequestClosedEvent extends TimelineEvent {
  type: TimelineEventType.ArticleInformalFeedbackRequestClosed
  user: TimelineEventInitiator
  data: {
    request_id: number
    reason: ArticleInformalFeedbackRequestClosedEventReason
    request: {
      id: number
      key: string
    }
    message: string | null
    reviews: {
      id: number
      reviewer: {
        id: number | null
        display_name: string
      }
      state: InformalFeedbackReviewState
    }[]
    branch?: {
      key: string
      title: string
      url: string
    }
  }
}

/*
 * Approval requests
 */
export interface ArticleReviewRequestEvent extends TimelineEvent {
  type: TimelineEventType.ArticleReviewRequest
  user: TimelineEventInitiator
  data: {
    reviewer: {
      id: number | null
      display_name: string | null
    }
    user_review: {
      message: string | null
    }
    base_branch: {
      key: string
      title: string
      url: string
    }
    branch: {
      key: string
      title: string
      url: string
    }
  }
}

export interface ArticleReviewRejectionEvent extends TimelineEvent {
  type: TimelineEventType.ArticleReviewRejection
  user: TimelineEventInitiator
  data: {
    message: string | null
    reviewer: {
      id: number | null
      display_name: string | null
    }
    requester: {
      id: number | null
      display_name: string | null
    }
  }
}

export interface ArticleReviewRequestApprovedAndMergedEvent extends TimelineEvent {
  type: TimelineEventType.ArticleReviewRequestApprovedAndMerged
  user: TimelineEventInitiator
  data: {
    message: string | null
    reviewer: {
      id: number
      display_name: string
    }
    branch: {
      key: string
      url: string
      title: string
    }
    base_branch: {
      key: string
      url: string
      title: string
    }
  }
}

export interface ArticleReviewCancelEvent extends TimelineEvent {
  type: TimelineEventType.ArticleReviewCancel
  user: TimelineEventInitiator
  data: {
    base_branch: {
      key: string
      title: string
      url: string
    }
    branch: {
      key: string
      title: string
      url: string
    }
  }
}

/*
 * Read receipts
 */
export interface ArticleReadReceiptRequestEvent extends TimelineEvent {
  type: TimelineEventType.ArticleReadReceiptRequest
  user: TimelineEventInitiator
  data: {
    request_id: number
    requestees: {
      display_name: string
    }[]
    message: string | null
  }
}

export interface ArticleEndReadReceiptEvent extends TimelineEvent {
  type: TimelineEventType.ArticleEndReadReceipt
  user: TimelineEventInitiator
  data: {
    feedback_request: {
      completed_task_count: number
      ended_on_due_date: boolean | null
      request_closer_name: string | null
      task_count: number
    }
  }
}

export interface ArticleMarkedAsReadEvent extends TimelineEvent {
  type: TimelineEventType.ArticleMarkedAsRead
  user: TimelineEventInitiator
  data: {}
}

export interface ArticleAllTasksMarkedAsReadEvent extends TimelineEvent {
  type: TimelineEventType.ArticleAllTasksMarkedAsRead
  user: TimelineEventInitiator
  data: {
    task_count: number
    request_id: number
  }
}

/*
 * Versioning
 */
export interface ArticleBranchCreatedEvent extends TimelineEvent {
  type: TimelineEventType.ArticleBranchCreated
  user: TimelineEventInitiator
  data: {
    base_branch: {
      key: string
      title: string
      url: string
    }
    branch: {
      key: string
      title: string
      url: string
    }
    status_name: string | null
    status_color: string | null
  }
}

export interface ArticleMergedEvent extends TimelineEvent {
  type: TimelineEventType.ArticleMerged
  user: TimelineEventInitiator
  data: {
    base_branch: {
      key: string
      title: string
      url: string
    }
    branch: {
      key: string
      title: string
      url: string
    }
    general_comment: string
  }
}

export interface ArticleMergedIntoEvent extends Omit<ArticleMergedEvent, 'type'> {
  type: TimelineEventType.ArticleMergedInto
}

export interface ArticleEventRestoredEvent extends TimelineEvent {
  type: TimelineEventType.ArticleEventRestored
  user: TimelineEventInitiator
  data: {
    restored_event: {
      timestamp: string
      name: string | null
    }
    from_version: number
    to_version: number
  }
}

/*
 * Origin
 */
export interface ArticleCreatedEvent extends TimelineEvent {
  type: TimelineEventType.ArticleCreated
  user: TimelineEventInitiator
  data: {
    status_name: string | null
    status_color: string | null
  }
}

export interface ArticleAIGeneratedEvent extends TimelineEvent {
  type: TimelineEventType.ArticleAIGeneratedEvent
  user: TimelineEventInitiator
  data: {
    generation_type: string | null
    request_id: string | null
    action: string | null
    topic: string | null
    product_name: string | null
    goal: string | null
  }
}

export interface ArticleAIRevisionAcceptedEvent extends TimelineEvent {
  type: TimelineEventType.ArticleAIRevisionAcceptedEvent
  user: TimelineEventInitiator
  data: {
    revision_type: RevisionType
    custom_prompt: string | null
    action: string | null
    accept_action: RevisionAcceptAction
    revised_content_type: string
    versions: number[]
  }
}

export interface ArticleCopyEvent extends TimelineEvent {
  type: TimelineEventType.ArticleCopy
  user: TimelineEventInitiator
  data: {
    source_article: {
      key: string
      url: string
      title: string
    }
    status_name: string | null
    status_color: string | null
  }
}

/*
 * Sharing
 */
export interface GrantArticleAccessEvent extends TimelineEvent {
  type: TimelineEventType.GrantArticleAccess
  user: TimelineEventInitiator
  data: {
    invitee: {
      email: string
      type: string // Check if enum is better
      id: number | null
      name: string
      avatar_url: string | null
    }
    permission: string
  }
}

export interface ArticleLastViewedEvent extends TimelineEvent {
  type: TimelineEventType.ArticleLastViewed
  user: TimelineEventInitiator
  data: {}
}

/*
 * Todos
 */
export interface CreateTodoEvent extends TimelineEvent {
  type: TimelineEventType.CreateTodo
  user: TimelineEventInitiator
  data: {
    todo_id: number
  }
}

export interface CompleteTodoEvent extends TimelineEvent {
  type: TimelineEventType.CompleteTodo
  user: TimelineEventInitiator
  data: {
    todo_id: number
  }
}

export interface ActivityEventPosition {
  /*
   * Whether this is the last event in the timeline.
   */
  isLast?: boolean
}

export interface ActivityEventSelection {
  /*
   * Whether this event is currently selected. Depending on the context, this
   * is used to highlight the corresponding UI.
   */
  isSelected?: boolean

  position?: ComparePosition
  /**
   * Whether this event cannot be selected
   */
  isDisabled?: boolean
}

export interface ActivityEventMouseHandlers {
  onClick?: (mouseEvent: any, timelineEvent: TimelineEvent) => void
}

export enum ActivityEventTrackMode {
  /*
   * Whether the event is a branching event. It will be shown like so:
   *
   * |   [ ] <-- Icon
   * | __/
   * |/
   */
  Branch = 'branch',

  /*
   * Whether the event is a branching event. It will be shown like so:
   *
   * |\__
   * |   \
   * |   [ ] <-- Icon
   */
  Merge = 'merge',
}

export interface BaseActivityEventProps extends ActivityEventPosition, ActivityEventSelection {
  /*
   * Icon to show on the left handside. Each type of ActivityEvent has a
   * dedicated icon.
   */
  icon: ReactNode

  /*
   * The content of the activity event, usually a description of the event and
   * who is involved.
   */
  content: ReactNode

  /*
   * The name of the event, if present.
   */
  name?: ReactNode

  /*
   * The timestamp of the event.
   */
  timestamp: string

  /*
   * The message attached to the event by one of the actors involved, if
   * present.
   */
  message?: ReactNode

  /*
   * The content to render in the collapsible section of the event. This is
   * only applicable to those events that group individual events, like
   * comments or edits.
   */
  nestedContent?: ReactNode

  /*
   * A set of icon buttons for secondary actions, to render on the right side.
   */
  actions?: ReactNode

  /*
   * Whether the icon should be displayed as branching out, merging in, or neither.
   */
  trackMode?: ActivityEventTrackMode

  /*
   * Callback to call then clicking on the event.
   */
  onClick?: (mouseEvent: any) => void

  /*
   * Whether actions are enabled and should be rendered.
   */
  actionsEnabled?: boolean

  /*
   * Whether the event should "flash" when appearing on the screen. Useful to
   * highlight new events coming in in realtime.
   */
  flashOnAppear?: boolean

  /*
   * Whether the nested content should be open by default.
   */
  initiallyOpen?: boolean

  /*
   * Class applied to the container
   */
  className?: string

  /*
   * Buttons to show below the content
   */
  buttons?: ReactNode

  /*
   * Classname for the left rail
   */
  leftRailClassName?: string

  /**
   * Optional status to display below the text
   */
  status?: { name: string; color: string }

  /**
   * Optional how to format the timestamp
   */
  timestampFormat?: string

  /**
   * Optional whether or not to display the timestamp, default: true
   */
  showTimestamp?: boolean

  /**
   * Optional whether or not to have hover props, default: false
   */
  isStatic?: boolean
}

export interface NestedActivityEventProps extends ActivityEventSelection, ActivityEventMouseHandlers {
  avatar: ReactNode
  content: ReactNode
  timestamp: string
  message?: ReactNode
  onClick?: (mouseEvent: any) => void
}

export type ActivityEventContext = 'review' | 'activity'

export interface LayerCreatedEvent extends TimelineEvent {
  type: TimelineEventType.LayerCreated
  user: TimelineEventInitiator
  data: {
    created_layer_name: string
    created_layer_id: string
    created_layer_color: string
  }
}

export interface LayerDeletedEvent extends TimelineEvent {
  type: TimelineEventType.LayerDeleted
  user: TimelineEventInitiator
  data: {
    deleted_layer_name: string
    deleted_layer_id: string
  }
}

export interface LayerMergedEvent extends TimelineEvent {
  type: TimelineEventType.LayerMerged
  user: TimelineEventInitiator
  data: {
    note?: string
    tracked_changes_count: number
    comments_count: number
    merged_layer_id: string
    parent_layer_id: string
    merged_layer_name: string
    merge_version_from: number
    merge_version_to: number
    merged_layer_color: string
  }
}

export interface WorkflowStatusUpdatedEvent extends TimelineEvent {
  type: TimelineEventType.WorkflowStatusUpdated
  user: TimelineEventInitiator
  data: {
    prev_status_color: string
    prev_status_name: string
    next_status_color: string
    next_status_name: string
  }
}

export interface AlmanacFileVcmChangedEvent extends TimelineEvent {
  type: TimelineEventType.VcmChanged
  user: TimelineEventInitiator
  data: {
    previous_value: boolean
    new_value: boolean
  }
}

export type ActivityEvent =
  | EditsGroupEvent
  | CommentsGroupEvent
  | RepliesGroupEvent
  | ArticleSuggestionReviewsCreatedEvent
  | ArticleSuggestionReviewRejectedEvent
  | ArticleSuggestionApprovedAndMergedEvent
  | ArticleSuggestionCancelledEvent
  | ArticleInformalFeedbackReviewsCreatedEvent
  | ArticleInformalFeedbackViewersCoCreatedEvent
  | ArticleInformalFeedbackViewersCreatedEvent
  | ArticleInformalFeedbackReviewSubmittedEvent
  | ArticleInformalFeedbackReviewDismissedEvent
  | ArticleInformalFeedbackRequestClosedEvent
  | ArticleReviewRequestEvent
  | ArticleReviewRejectionEvent
  | ArticleReviewRequestApprovedAndMergedEvent
  | ArticleReviewCancelEvent
  | ArticleReadReceiptRequestEvent
  | ArticleAllTasksMarkedAsReadEvent
  | ArticleEndReadReceiptEvent
  | ArticleMarkedAsReadEvent
  | ArticleBranchCreatedEvent
  | ArticleMergedEvent
  | ArticleMergedIntoEvent
  | ArticleCreatedEvent
  | ArticleCopyEvent
  | GrantArticleAccessEvent
  | ArticleLastViewedEvent
  | ArticleEventRestoredEvent
  | CreateTodoEvent
  | CompleteTodoEvent
  | LayerDeletedEvent
  | LayerCreatedEvent
  | LayerMergedEvent
  | WorkflowStatusUpdatedEvent
  | AlmanacFileVcmChangedEvent
  | ArticleAIGeneratedEvent
  | ArticleAIRevisionAcceptedEvent

export interface ActivityEventProps extends ActivityEventPosition, ActivityEventSelection, ActivityEventMouseHandlers {
  event: ActivityEvent
  actionsEnabled?: boolean
  isDisabled?: boolean
  context?: ActivityEventContext
}

export type ActionMenuHandler = (actionModalContext: ActivityActionModalContextValue, event: TimelineEvent) => void
export type ActionMenuHandlers = { [key: string]: ActionMenuHandler }
